/**
 * Offering catalog field validation (OU-1g.a).
 *
 * Lives in `/shared` (CLAUDE.md §26) so the admin form (`OfferingsPage`) and the
 * authoritative backend route validate against ONE schema. The PA/MA-type set
 * reuses the existing `OFFERING_TYPES` constant from `constants.js` (no magic
 * strings, no duplicate definition), and `termLabel` reuses `TERM_LABEL_REGEX`
 * from the schoolyear validation so the "YYYY/YY-N" convention is fixed in a
 * single place (ADR `docs/decisions/schoolyear-und-halbjahr.md`).
 */
import { z } from 'zod';
import { OFFERING_TYPES } from './constants.js';
import { TERM_LABEL_REGEX } from './schoolyear-validation.js';
export const OfferingTypeSchema = z.nativeEnum(OFFERING_TYPES);
const nameField = z
    .string()
    .trim()
    .min(1, 'Name erforderlich.')
    .max(120, 'Name darf höchstens 120 Zeichen lang sein.');
const idField = z.number().int().positive();
const termLabelField = z
    .string()
    .regex(TERM_LABEL_REGEX, 'Halbjahr muss im Format "YYYY/YY-N" sein, z. B. "2025/26-1".');
/**
 * Create payload. `roomId`/`teacherId` are optional + nullable — a (MA-)Offering
 * may have neither a fixed room nor a fixed teacher (no XOR constraint).
 */
export const OfferingCreateSchema = z.object({
    name: nameField,
    type: OfferingTypeSchema,
    roomId: idField.nullable().optional(),
    teacherId: idField.nullable().optional(),
    termLabel: termLabelField,
    active: z.boolean().default(true),
});
/**
 * Update payload — every field optional (partial PATCH). `active` is
 * deliberately EXCLUDED: a status toggle goes exclusively through the dedicated
 * `PATCH /:id/active` route (`OfferingActiveSchema`) so its audit semantics
 * (`offering_deactivated`/`offering_reactivated`) never collapse into a generic
 * `offering_updated` event.
 */
export const OfferingUpdateSchema = OfferingCreateSchema.omit({ active: true }).partial();
/** Soft-delete toggle payload for `PATCH /:id/active`. */
export const OfferingActiveSchema = z.object({ active: z.boolean() });
//# sourceMappingURL=offering-validation.js.map